/**
 * Tiny reactive wrapper around `ClassicalBallTracker` for the Svelte runes
 * world. Holds a bounded trail of the last N detections so the UI can draw
 * a fading tail.
 *
 * The state object itself is a plain object; callers are expected to
 * assign/replace the returned `state` inside a `$state(...)` rune and then
 * reassign the fields returned from `feed()` — see `TestVideo.svelte` for
 * the exact integration pattern.
 *
 * Kept framework-agnostic so it can be unit-tested in plain jsdom without
 * mounting a component.
 */
import { ClassicalBallTracker, type BallCandidate } from './classicalBallTracker.js';

export interface BallLiveState {
  current: BallCandidate | null;
  trail: readonly BallCandidate[];
}

export interface BallLiveController {
  readonly state: BallLiveState;
  feed(
    frame: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
    timestampMs: number,
  ): BallCandidate | null;
  reset(): void;
  dispose(): void;
}

export const BALL_TRAIL_LENGTH = 20;

export function createBallLiveState(
  tracker: ClassicalBallTracker = new ClassicalBallTracker(),
): BallLiveController {
  // Mutable internal state — consumers wrap this object in `$state(...)` at
  // the caller site so the Svelte reactivity layer picks up field writes.
  const state: BallLiveState = {
    current: null,
    trail: [],
  };
  let trailBuf: BallCandidate[] = [];

  return {
    state,
    feed(frame, timestampMs) {
      const detection = tracker.ingest(frame, timestampMs);
      state.current = detection;
      if (detection) {
        trailBuf.push(detection);
        if (trailBuf.length > BALL_TRAIL_LENGTH) {
          trailBuf = trailBuf.slice(trailBuf.length - BALL_TRAIL_LENGTH);
        }
        // Replace the readonly trail with a fresh array so reactivity fires.
        state.trail = trailBuf.slice();
      }
      return detection;
    },
    reset() {
      tracker.reset();
      trailBuf = [];
      state.current = null;
      state.trail = [];
    },
    dispose() {
      tracker.dispose();
      trailBuf = [];
      state.current = null;
      state.trail = [];
    },
  };
}
